// @flow
import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router';
import styles from './GetPremiumButton.css';
import * as uiActions from '../../actions/ui';
import * as analyticsActions from '../../actions/analytics';
import { selectIsInIOSApp, selectPageTrackingContext } from '../../selectors/client';
import { connect } from 'react-redux';
import { NavigationPlugin } from '../../capacitor-connector';
import { selectIsTrialAvailable } from '../../selectors/subscription';
import IconLabel from '../util/IconLabel';
import CapacitorRipple from '../capacitor/Ripple';
import { defineMessages, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

const messages = defineMessages({
  getPremium: {
    id: 'global.cta.get-premium',
    defaultMessage: 'Get IDAGIO Premium',
  },
  tryPremium: {
    id: 'global.cta.try-premium-free',
    defaultMessage: 'Try Premium For Free',
  },
});

type OwnProps = {
  'trackingContext': Object,
  'className'?: string,
  'data-test'?: string,
  'onClick'?: Function,
  'targetPlan'?: string,
  'showIcon'?: boolean,
  'showText'?: boolean,
  'iconName'?: string,
  'iconSize'?: 'small' | 'default' | 'medium' | 'large' | string,
  'text'?: string,
};

type MapStateToProps = {
  isInIOSApp: boolean,
  pageTrackingContext: Object,
  isTrialAvailable: boolean,
};

type DispatchProps = {
  showModal: Function,
  track: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps & { intl: IntlShape };

declare var __CAPACITOR__: boolean;
declare var __ELECTRON__: boolean;

const GetPremiumButton = ({
  className,
  'data-test': dataTest,
  onClick,
  targetPlan,
  showModal,
  isInIOSApp,
  isTrialAvailable,
  track,
  trackingContext,
  pageTrackingContext,
  showIcon = true,
  showText = true,
  iconName = 'premium-transparent',
  iconSize,
  text,
  intl,
}: Props) => {
  const isElectronOrIOS = __ELECTRON__ || isInIOSApp;

  const onSubscribeClick = () => {
    if (onClick) {
      onClick();
    }

    if (isElectronOrIOS) {
      track('Get Premium Modal Triggered', { ...trackingContext, ...pageTrackingContext });
      return;
    }

    if (__CAPACITOR__) {
      NavigationPlugin.androidShouldOpenSubscriptionPage();
      return;
    }

    showModal('SUBSCRIBE_MODAL', trackingContext, targetPlan ? { plan: targetPlan } : {});
  };

  const defaultText = isTrialAvailable
    ? intl.formatMessage(messages.tryPremium)
    : intl.formatMessage(messages.getPremium);
  const textToShow = text || defaultText;

  const GetPremium = (
    <React.Fragment>
      {showIcon && <IconLabel name={iconName} size={iconSize || (showText ? 'small' : 'large')} />}
      {showText && textToShow}
    </React.Fragment>
  );

  const btnClassName = classNames(className, styles.button);

  if (isElectronOrIOS) {
    return (
      <Link
        className={btnClassName}
        to={`/subscribe${targetPlan ? `?plan=${targetPlan}` : ''}`}
        data-test={dataTest}
        onClick={onSubscribeClick}
      >
        {GetPremium}
      </Link>
    );
  }

  return (
    <button
      className={btnClassName}
      onClick={onSubscribeClick}
      data-test={dataTest}
      aria-label={!showText ? textToShow : null}
    >
      <CapacitorRipple />
      {GetPremium}
    </button>
  );
};

function mapStateToProps(state: Object): MapStateToProps {
  return {
    isInIOSApp: selectIsInIOSApp(state),
    pageTrackingContext: selectPageTrackingContext(state),
    isTrialAvailable: selectIsTrialAvailable(state),
  };
}

const dispatchProps: DispatchProps = {
  showModal: uiActions.showModal,
  track: analyticsActions.track,
};

export default connect(mapStateToProps, dispatchProps)(injectIntl(GetPremiumButton));
